import { useCallback, useEffect, useRef, useState } from "react";
import { Tags, Button, Text } from "../../../components/ui";
import hydrationVestImage from "../assets/hydration_vest.svg";
import minimizeGlyph from "../assets/minimize.svg";
import { resolveRecoImageSrc } from "../utils/resolveRecoImageSrc.js";
import { amazonUrlForRec } from "../utils/amazonLinks.js";
import RecoRatingStars from "./RecoRatingStars.jsx";
import RecoPrice from "./RecoPrice.jsx";
import s from "./ReminderRecoCarousel.module.css";

/**
 * Horizontal reco carousel (Figma 407:8513 / cards 320px). Dots track scroll when `showDots`.
 * `includeTopRecoPill`: brand-secondary “Top Recommendation” pill on first slide (primary section only in Figma).
 */
export default function ReminderRecoCarousel({
  reminder,
  recommendations,
  gifteeId,
  navigate,
  includeTopRecoPill = true,
  showDots = true,
}) {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [collapsedCardIds, setCollapsedCardIds] = useState(() => new Set());
  const [imageFallbackIds, setImageFallbackIds] = useState(() => new Set());
  const slideCount = recommendations.length + 1;

  const markImageFallback = useCallback((cardId) => {
    setImageFallbackIds((prev) => new Set(prev).add(cardId));
  }, []);

  const toggleCardView = useCallback((cardId) => {
    setCollapsedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const syncActiveFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || el.children.length === 0) return;
    const n = el.children.length;
    const centerX = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < n; i++) {
      const child = el.children[i];
      const left = child.offsetLeft;
      const mid = left + child.offsetWidth / 2;
      const d = Math.abs(centerX - mid);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setActiveIndex(Math.min(n - 1, Math.max(0, best)));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    syncActiveFromScroll();
    el.addEventListener("scroll", syncActiveFromScroll, { passive: true });
    el.addEventListener("scrollend", syncActiveFromScroll);
    const ro = new ResizeObserver(() => {
      syncActiveFromScroll();
    });
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", syncActiveFromScroll);
      el.removeEventListener("scrollend", syncActiveFromScroll);
      ro.disconnect();
    };
  }, [syncActiveFromScroll]);

  return (
    <div className={s.carouselRoot}>
      <Text
        variant="paragraphSm"
        color="var(--gg-grey-muted)"
        className={s.affiliateDisclosure}
      >
        We may earn a commission from purchases made through these links, at
        no additional cost to you.
      </Text>
      <div
        ref={scrollerRef}
        className={s.recommendationScroller}
        data-horizontal-pan="true"
      >
        {recommendations.map((rec, recIdx) => (
          (() => {
            const cardId = `${reminder.id}-${rec.id}`;
            const isCollapsed = collapsedCardIds.has(cardId);
            const sizes = rec.sizes;
            const colors = rec.colors;
            const brand = rec.brand || "Gift Gaff";
            const productUrl = amazonUrlForRec(rec);
            const imageSrc = imageFallbackIds.has(cardId)
              ? hydrationVestImage
              : resolveRecoImageSrc(rec, hydrationVestImage);

            return (
              <article
                key={cardId}
                className={`${s.miniCard} ${isCollapsed ? s.compactRecoCard : s.featuredRecoSlide}`}
              >
                {isCollapsed ? (
                  <>
                    {includeTopRecoPill && recIdx === 0 ? (
                      <Tags
                        className={s.compactTopRecoTag}
                        label="Top Recommendation"
                        color="brandSecondary"
                        variant="filled"
                      />
                    ) : null}
                    <button
                      type="button"
                      className={s.compactCornerBtn}
                      aria-label={`Expand details for ${rec.title}`}
                      onClick={() => toggleCardView(cardId)}
                    >
                      <img src={minimizeGlyph} alt="" className={s.compactCornerGlyph} />
                    </button>
                    <div className={s.compactRecoBody}>
                      <div className={s.compactTopBlock}>
                        <p className={s.compactBrand}>{brand.toUpperCase()}</p>
                        <h3 className={s.compactTitle}>{rec.title}</h3>
                        <RecoPrice priceText={rec.priceText} className={s.compactPrice} />
                      </div>

                      <div className={s.compactSpecsStack}>
                        {sizes?.length ? (
                          <div className={s.compactSpecGroup}>
                            <p className={s.compactSpecLabel}>Sizes</p>
                            <div className={s.compactChipRow}>
                              {sizes.map((size) => (
                                <span key={`${cardId}-${size}`} className={s.compactChip}>
                                  {size}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {colors?.length ? (
                          <div className={s.compactSpecGroup}>
                            <p className={s.compactSpecLabel}>Colors</p>
                            <div className={s.compactChipRow}>
                              {colors.map((color) => (
                                <span key={`${cardId}-${color}`} className={s.compactChip}>
                                  {color}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {(rec.tags || []).length ? (
                          <div className={s.compactSpecGroup}>
                            <p className={s.compactSpecLabel}>Interests</p>
                            <div className={s.compactChipRow}>
                              {rec.tags.map((tag) => (
                                <span key={`${cardId}-t-${tag}`} className={s.compactChip}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <div className={s.compactSpecGroup}>
                          <p className={s.compactSpecLabel}>Brand</p>
                          <div className={s.compactChipRow}>
                            <span className={s.compactChip}>{brand}</span>
                          </div>
                        </div>

                        <RecoRatingStars
                          rating={rec.rating}
                          reviewCount={rec.reviewCount}
                        />
                      </div>
                    </div>
                    <Button
                      className={s.recoCardCta}
                      variant="primary"
                      fullWidth
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      aria-label={`Buy ${rec.fullTitle || rec.title} on Amazon`}
                    >
                      Buy on Amazon
                    </Button>
                  </>
                ) : (
                  <>
                    {includeTopRecoPill && recIdx === 0 ? (
                      <Tags
                        className={s.compactTopRecoTag}
                        label="Top Recommendation"
                        color="brandSecondary"
                        variant="filled"
                      />
                    ) : null}
                    <div className={s.featureImageBlock}>
                      <button
                        type="button"
                        className={s.imageCornerBtn}
                        aria-label={`Show compact details for ${rec.title}`}
                        onClick={() => toggleCardView(cardId)}
                      >
                        <img src={minimizeGlyph} alt="" className={s.imageCornerGlyph} />
                      </button>
                      <img
                        src={imageSrc}
                        alt=""
                        aria-hidden="true"
                        className={s.productImage}
                        onError={() => markImageFallback(cardId)}
                      />
                    </div>
                    <div className={s.tagRow}>
                      {(rec.tags || []).map((tag) => (
                        <Tags
                          key={`${rec.id}-${tag}`}
                          label={tag}
                          color="brandPrimary"
                          variant="filled"
                        />
                      ))}
                    </div>
                    <div className={s.featureBody}>
                      <div className={s.frontRecoCopy}>
                        <div className={s.frontRecoHeading}>
                          <p className={s.frontRecoBrand}>{brand.toUpperCase()}</p>
                          <h3 className={s.frontRecoTitle}>{rec.title}</h3>
                        </div>
                        <RecoPrice priceText={rec.priceText} className={s.frontRecoPrice} />
                      </div>
                    </div>
                    <Button
                      className={`${s.recoCardCta} ${s.frontRecoCardCta}`}
                      variant="primary"
                      fullWidth
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      aria-label={`Buy ${rec.fullTitle || rec.title} on Amazon`}
                    >
                      Buy on Amazon
                    </Button>
                  </>
                )}
              </article>
            );
          })()
        ))}
        <div className={s.viewAllSlide}>
          <article className={s.recoEndCard}>
            <div className={s.recoEndImageWrap}>
              <img
                src="/images/GiftGaff_Logo.svg"
                alt=""
                aria-hidden="true"
                className={s.recoEndLogo}
              />
            </div>
            <div className={s.recoEndBody}>
              <div className={s.recoEndCopy}>
                <p className={s.recoEndTitle}>That's all...</p>
                <p className={s.recoEndSubtitle}>But we can find more</p>
              </div>
              <button
                type="button"
                className={s.recoEndCta}
                onClick={() =>
                  navigate(`/giftee/${gifteeId}/more-recommendations`, {
                    state: { reminderTitle: reminder.title },
                  })
                }
              >
                <span>More Recommendations</span>
                <i className="fa-solid fa-plus" aria-hidden="true" />
              </button>
            </div>
          </article>
        </div>
      </div>
      {showDots ? (
        <div
          className={s.carouselDots}
          role="status"
          aria-label={`Recommendation ${activeIndex + 1} of ${slideCount}`}
        >
          {Array.from({ length: slideCount }).map((_, i) => (
            <span
              key={`dot-${reminder.id}-${i}`}
              className={i === activeIndex ? s.dotActive : s.dotIdle}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
